import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Category detection helpers ──
 * iter268: STRICT gating, no fuzzy `includes()` matching. Previously
 * `includes('phone') || includes('mobile')` made the phone form fire on
 * `electronics` (because `electronics_accessories` matched none of the
 * allowlist but did NOT contain 'phone' — wait, it did via 'phone' in 'phones'
 * being checked separately, but the real bug was 'electronics' being in
 * PHONE_CATS). Mobile and Electronics are SEPARATE parents — the phone
 * form must NEVER fire on Electronics ads, and vice versa.
 *
 * The set entries below are kept narrow on purpose. If the user picks
 * Electronics → TV → Audio, no phone-specific dynamic form will render.
 */
const PHONE_CATS = new Set([
  'mobile_accessories',           // canonical parent
  'smartphones',                  // canonical sub
  'tablets',                      // canonical sub
  'phone_accessories',            // canonical sub
  // Historical aliases the data layer may still emit:
  'mobile', 'mobiles', 'phones', 'phone', 'smartphone',
]);
const CAR_CATS = new Set([
  'vehicles',                     // canonical parent
  'vehicles_motorbikes',          // historical alias
  'cars',                         // canonical sub
  'motorcycles',                  // canonical sub
  'auto_parts',                   // canonical sub
  'heavy_vehicles',               // canonical sub
]);
const RE_CATS = new Set([
  'real_estate',
  're_apartments', 're_houses', 're_land', 're_commercial',
  // Historical:
  'apartment', 'house', 'land', 'commercial',
]);

export function detectVertical(categoryKey, subcategoryKey) {
  const c = (categoryKey || '').toLowerCase().trim();
  const s = (subcategoryKey || '').toLowerCase().trim();
  // STRICT: the parent must match exactly. We additionally accept a sub
  // match so backwards-compat callers passing only the legacy
  // `category='cars'` still get the car form. No fuzzy includes().
  if (PHONE_CATS.has(c) || PHONE_CATS.has(s)) return 'phone';
  if (CAR_CATS.has(c) || CAR_CATS.has(s)) return 'car';
  if (RE_CATS.has(c) || RE_CATS.has(s)) return 'real_estate';
  return null;
}

/* ── Localized option maps ── */
const CONDITION_OPTIONS = [
  { value: 'New', en: 'New', ar: 'جديد' },
  { value: 'Used - Like New', en: 'Used – Like New', ar: 'مستعمل - كالجديد' },
  { value: 'Used - Excellent', en: 'Used – Excellent', ar: 'مستعمل - ممتاز' },
  { value: 'Used - Good', en: 'Used – Good', ar: 'مستعمل - جيد' },
  { value: 'Used - Fair', en: 'Used – Fair', ar: 'مستعمل - مقبول' },
  { value: 'For Parts', en: 'For Parts', ar: 'للقطع' }
];

const CAR_CONDITION_OPTIONS = [
  { value: 'New', en: 'New', ar: 'جديد' },
  { value: 'Used - Excellent', en: 'Used – Excellent', ar: 'مستعمل - ممتاز' },
  { value: 'Used - Good', en: 'Used – Good', ar: 'مستعمل - جيد' },
  { value: 'Used - Fair', en: 'Used – Fair', ar: 'مستعمل - مقبول' },
  { value: 'Salvage', en: 'Salvage', ar: 'تالف' },
  { value: 'Other', en: 'Other', ar: 'أخرى' }
];

const FUEL_OPTIONS = [
  { value: 'Petrol', en: 'Petrol', ar: 'بنزين' },
  { value: 'Diesel', en: 'Diesel', ar: 'ديزل' },
  { value: 'Hybrid', en: 'Hybrid', ar: 'هجين' },
  { value: 'Electric', en: 'Electric', ar: 'كهربائي' },
  { value: 'Other', en: 'Other', ar: 'أخرى' }
];

const TRANSMISSION_OPTIONS = [
  { value: 'Automatic', en: 'Automatic', ar: 'أوتوماتيك' },
  { value: 'Manual', en: 'Manual', ar: 'عادي' }
];

const BODY_TYPE_OPTIONS = [
  { value: 'Sedan', en: 'Sedan', ar: 'سيدان' },
  { value: 'SUV', en: 'SUV', ar: 'دفع رباعي' },
  { value: 'Pickup', en: 'Pickup', ar: 'بيك أب' },
  { value: 'Hatchback', en: 'Hatchback', ar: 'هاتشباك' },
  { value: 'Van', en: 'Van', ar: 'فان' },
  { value: 'Coupe', en: 'Coupe', ar: 'كوبيه' },
  { value: 'Other', en: 'Other', ar: 'أخرى' }
];

const PROPERTY_TYPE_OPTIONS = [
  { value: 'Apartment', en: 'Apartment', ar: 'شقة' },
  { value: 'House', en: 'House', ar: 'منزل' },
  { value: 'Villa', en: 'Villa', ar: 'فيلا' },
  { value: 'Studio', en: 'Studio', ar: 'استوديو' },
  { value: 'Land', en: 'Land', ar: 'أرض' },
  { value: 'Commercial', en: 'Commercial', ar: 'تجاري' },
  { value: 'Other', en: 'Other', ar: 'أخرى' }
];

const STORAGE_OPTIONS = ['32GB', '64GB', '128GB', '256GB', '512GB', '1TB'];

/* ────────────────────────────────────────────
   Unified Bottom Sheet Select Component
   Matches the SelectField/SelectSheet design
──────────────────────────────────────────── */
function SheetSelect({ value, onChange, options, placeholder, title, isArabic, testId, disabled, showOtherInput, otherValue, onOtherChange }) {
  const [open, setOpen] = useState(false);
  const sheetRef = useRef(null);

  const getLabel = useCallback((o) => {
    if (typeof o === 'string') return o;
    return isArabic ? o.ar : o.en;
  }, [isArabic]);

  const getValue = useCallback((o) => {
    if (typeof o === 'string') return o;
    return o.value;
  }, []);

  const selectedLabel = value
    ? getLabel(options.find(o => getValue(o) === value) || value)
    : null;

  // Close on backdrop click
  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget) setOpen(false);
  }, []);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        className={`vaf-sheet-trigger ${value ? 'has-value' : ''} ${disabled ? 'is-disabled' : ''}`}
        onClick={() => !disabled && setOpen(true)}
        data-testid={testId}
        disabled={disabled}
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        <span className={`vaf-trigger-text ${!selectedLabel ? 'placeholder' : ''}`}>
          {selectedLabel || placeholder}
        </span>
        <svg className="vaf-trigger-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* "Other" follow-up input */}
      {showOtherInput && value === 'Other' && (
        <input
          type="text"
          className="input vaf-other-input"
          value={otherValue || ''}
          onChange={(e) => onOtherChange && onOtherChange(e.target.value)}
          placeholder={isArabic ? 'يرجى التحديد' : 'Please specify'}
          dir={isArabic ? 'rtl' : 'ltr'}
          data-testid={`${testId}-other-input`}
          style={{ marginTop: 8 }}
        />
      )}

      {/* Bottom Sheet Overlay */}
      {open && (
        <div className="vaf-sheet-overlay" onClick={handleBackdrop}>
          <div
            className="vaf-sheet-container"
            ref={sheetRef}
            dir={isArabic ? 'rtl' : 'ltr'}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="vaf-sheet-handle-area">
              <div className="vaf-sheet-handle" />
            </div>

            {/* Header */}
            <div className="vaf-sheet-header">
              <h3 className="vaf-sheet-title">{title || placeholder}</h3>
              <button
                type="button"
                className="vaf-sheet-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Options list */}
            <div className="vaf-sheet-list">
              {options.map((o) => {
                const val = getValue(o);
                const label = getLabel(o);
                const isSelected = val === value;

                return (
                  <button
                    key={val}
                    type="button"
                    className={`vaf-sheet-option ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => {
                      onChange(val);
                      setOpen(false);
                    }}
                    data-testid={`${testId}-option-${val}`}
                  >
                    <span className="vaf-option-label">{label}</span>
                    {isSelected && (
                      <span className="vaf-option-check">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Field wrapper ── */
function Field({ label, children, testId, isArabic }) {
  return (
    <div className="row" dir={isArabic ? 'rtl' : 'ltr'} style={{ marginBottom: 14 }}>
      <label className="label">{label}</label>
      <div data-testid={testId}>{children}</div>
    </div>
  );
}

/* ── Phone Form ── */
function PhoneAttrsForm({ attrs, onChange, isArabic }) {
  const [catalog, setCatalog] = useState({});
  const brands = Object.keys(catalog);
  const models = catalog[attrs.phone_brand] || [];

  // Add "Other" to brands and models
  const brandOptions = [...brands.map(b => ({ value: b, en: b, ar: b })), { value: 'Other', en: 'Other', ar: 'أخرى' }];
  const modelOptions = [...models.map(m => ({ value: m, en: m, ar: m })), { value: 'Other', en: 'Other', ar: 'أخرى' }];

  useEffect(() => {
    fetch(`${API_URL}/api/phones/catalog`)
      .then(r => r.json())
      .then(d => setCatalog(d.brands || {}))
      .catch(() => {});
  }, []);

  const set = (key, val) => {
    const next = { ...attrs, [key]: val };
    if (key === 'phone_brand') { next.phone_model = ''; next.phone_brand_other = ''; }
    if (key === 'phone_model') { next.phone_model_other = ''; }
    onChange(next);
  };

  return (
    <div className="vertical-attrs-section" data-testid="phone-attrs-form">
      <div className="vertical-attrs-header">
        <span className="vertical-attrs-title">
          {isArabic ? 'تفاصيل الهاتف' : 'Phone Details'}
        </span>
      </div>

      <Field label={isArabic ? 'العلامة التجارية' : 'Brand'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.phone_brand}
          onChange={v => set('phone_brand', v)}
          options={brandOptions}
          placeholder={isArabic ? 'اختر العلامة' : 'Select brand'}
          title={isArabic ? 'العلامة التجارية' : 'Brand'}
          testId="phone-brand-select"
          isArabic={isArabic}
          showOtherInput={true}
          otherValue={attrs.phone_brand_other}
          onOtherChange={v => onChange({ ...attrs, phone_brand_other: v })}
        />
      </Field>

      <Field label={isArabic ? 'الموديل' : 'Model'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.phone_model}
          onChange={v => set('phone_model', v)}
          options={modelOptions}
          placeholder={isArabic ? 'اختر الموديل' : 'Select model'}
          title={isArabic ? 'الموديل' : 'Model'}
          testId="phone-model-select"
          disabled={!attrs.phone_brand}
          isArabic={isArabic}
          showOtherInput={true}
          otherValue={attrs.phone_model_other}
          onOtherChange={v => onChange({ ...attrs, phone_model_other: v })}
        />
      </Field>

      <Field label={isArabic ? 'التخزين' : 'Storage'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.storage}
          onChange={v => set('storage', v)}
          options={STORAGE_OPTIONS}
          placeholder={isArabic ? 'اختر' : 'Select'}
          title={isArabic ? 'التخزين' : 'Storage'}
          testId="phone-storage-select"
          isArabic={isArabic}
        />
      </Field>

      <Field label={isArabic ? 'الحالة' : 'Condition'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.condition}
          onChange={v => set('condition', v)}
          options={CONDITION_OPTIONS}
          placeholder={isArabic ? 'اختر الحالة' : 'Select condition'}
          title={isArabic ? 'الحالة' : 'Condition'}
          testId="phone-condition-select"
          isArabic={isArabic}
        />
      </Field>

      <Field label={isArabic ? 'صحة البطارية %' : 'Battery Health %'} isArabic={isArabic}>
        <input
          type="number"
          className="input"
          value={attrs.battery_health || ''}
          onChange={e => set('battery_health', e.target.value ? Number(e.target.value) : null)}
          placeholder="85"
          min="0"
          max="100"
          data-testid="phone-battery-input"
          dir={isArabic ? 'rtl' : 'ltr'}
        />
      </Field>
    </div>
  );
}

/* ── Vehicles & Motorbikes Form (iter287)
   Operator spec: a single hub-level "Vehicle Type" picker drives the
   brand catalog, so motorbike users never see Kia/BMW and vice versa.
   When `vehicle_type` is not yet set, we infer it from the subcategory
   so legacy ads (`auto_parts`, `motorcycles`, `heavy_vehicles`) keep
   working with the right brand list. Model becomes free-text for
   non-car types per the spec (no forced model dropdown). */
const VEHICLE_TYPE_OPTIONS = [
  { value: 'cars',         en: 'Cars',                       ar: 'سيارات' },
  { value: 'motorbikes',   en: 'Motorbikes',                 ar: 'دراجات نارية' },
  { value: 'tuktuk',       en: 'Tuk-tuk / Rickshaw',         ar: 'ركشة' },
  { value: 'trucks',       en: 'Trucks / Heavy Vehicles',    ar: 'شاحنات / آليات ثقيلة' },
  { value: 'spare_parts',  en: 'Spare Parts',                ar: 'قطع غيار' },
];

function inferVehicleTypeFromSubcategory(subcategory) {
  const sub = (subcategory || '').toLowerCase();
  if (sub.includes('motorcycle') || sub.includes('motorbike') || sub.includes('دراج')) return 'motorbikes';
  if (sub.includes('tuktuk') || sub.includes('rickshaw') || sub.includes('ركشة')) return 'tuktuk';
  if (sub.includes('heavy') || sub.includes('truck') || sub.includes('شاحن')) return 'trucks';
  if (sub.includes('part') || sub.includes('قطع')) return 'spare_parts';
  return 'cars';
}

function CarAttrsForm({ attrs, onChange, isArabic, subcategory }) {
  const inferredType = inferVehicleTypeFromSubcategory(subcategory);
  const vehicleType = attrs.vehicle_type || inferredType;
  const isCars = vehicleType === 'cars';

  const [catalog, setCatalog] = useState({});
  const brands = Object.keys(catalog);
  const models = catalog[attrs.brand] || [];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 40 }, (_, i) => String(currentYear - i));

  // Add "Other" to brands and models so users can always free-text.
  const brandOptions = [
    ...brands.map(b => ({ value: b, en: b, ar: b })),
    { value: 'Other', en: 'Other', ar: 'أخرى' },
  ];
  const modelOptions = [
    ...models.map(m => ({ value: m, en: m, ar: m })),
    { value: 'Other', en: 'Other', ar: 'أخرى' },
  ];

  // iter287 — refetch the catalog every time the vehicle type changes
  // (and on first mount). The endpoint accepts `?type=` per iter287.
  useEffect(() => {
    fetch(`${API_URL}/api/cars/catalog?type=${encodeURIComponent(vehicleType)}`)
      .then(r => r.json())
      .then(d => setCatalog(d.brands || {}))
      .catch(() => {});
  }, [vehicleType]);

  // Backfill the inferred vehicle_type into attrs ONCE on first render
  // so the published ad carries it even if the user never opens the
  // Vehicle Type picker.
  useEffect(() => {
    if (!attrs.vehicle_type && inferredType) {
      onChange({ ...attrs, vehicle_type: inferredType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, val) => {
    const next = { ...attrs, [key]: val };
    if (key === 'brand')   { next.model = ''; next.brand_other = ''; next.model_other = ''; }
    if (key === 'model')   { next.model_other = ''; }
    // iter287 — changing vehicle type MUST clear brand/model so we
    // never publish e.g. vehicle_type=motorbikes with brand=Kia.
    if (key === 'vehicle_type') {
      next.brand = '';
      next.model = '';
      next.brand_other = '';
      next.model_other = '';
    }
    onChange(next);
  };

  // Title localised per vehicle type so the form header isn't always
  // "Car Details".
  const vt = VEHICLE_TYPE_OPTIONS.find(o => o.value === vehicleType) || VEHICLE_TYPE_OPTIONS[0];
  const headerEn = `${vt.en} Details`;
  const headerAr = `تفاصيل ${vt.ar}`;

  return (
    <div className="vertical-attrs-section" data-testid="car-attrs-form">
      <div className="vertical-attrs-header">
        <span className="vertical-attrs-title">
          {isArabic ? headerAr : headerEn}
        </span>
      </div>

      {/* iter287 — Vehicle Type is the FIRST required field. Drives
          the brand list below. */}
      <Field label={isArabic ? 'نوع المركبة' : 'Vehicle Type'} isArabic={isArabic}>
        <SheetSelect
          value={vehicleType}
          onChange={v => set('vehicle_type', v)}
          options={VEHICLE_TYPE_OPTIONS}
          placeholder={isArabic ? 'اختر نوع المركبة' : 'Select vehicle type'}
          title={isArabic ? 'نوع المركبة' : 'Vehicle Type'}
          testId="vehicle-type-select"
          isArabic={isArabic}
        />
      </Field>

      <Field
        label={
          isCars
            ? (isArabic ? 'العلامة التجارية' : 'Brand')
            : (isArabic ? 'العلامة التجارية (اختياري)' : 'Brand (optional)')
        }
        isArabic={isArabic}
      >
        <SheetSelect
          value={attrs.brand}
          onChange={v => set('brand', v)}
          options={brandOptions}
          placeholder={isArabic ? 'اختر العلامة' : 'Select brand'}
          title={isArabic ? 'العلامة التجارية' : 'Brand'}
          testId="car-brand-select"
          isArabic={isArabic}
          showOtherInput={true}
          otherValue={attrs.brand_other}
          onOtherChange={v => onChange({ ...attrs, brand_other: v })}
        />
      </Field>

      {/* iter287 — Model is REQUIRED-dropdown only for cars. For
          motorbikes / tuk-tuk / trucks / spare-parts the operator
          spec says model is OPTIONAL free-text — render a plain
          input so users aren't blocked by a forced enum. */}
      {isCars ? (
        <Field label={isArabic ? 'الموديل' : 'Model'} isArabic={isArabic}>
          <SheetSelect
            value={attrs.model}
            onChange={v => set('model', v)}
            options={modelOptions}
            placeholder={isArabic ? 'اختر الموديل' : 'Select model'}
            title={isArabic ? 'الموديل' : 'Model'}
            testId="car-model-select"
            disabled={!attrs.brand}
            isArabic={isArabic}
            showOtherInput={true}
            otherValue={attrs.model_other}
            onOtherChange={v => onChange({ ...attrs, model_other: v })}
          />
        </Field>
      ) : (
        <Field label={isArabic ? 'الموديل (اختياري)' : 'Model (optional)'} isArabic={isArabic}>
          <input
            type="text"
            className="input"
            value={attrs.model_other || attrs.model || ''}
            onChange={e => onChange({ ...attrs, model: e.target.value, model_other: e.target.value })}
            placeholder={isArabic ? 'مثال: Bajaj Boxer 150' : 'e.g. Bajaj Boxer 150'}
            data-testid="vehicle-model-input"
            dir={isArabic ? 'rtl' : 'ltr'}
          />
        </Field>
      )}

      <Field label={isArabic ? 'سنة الصنع' : 'Year'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.year}
          onChange={v => set('year', v)}
          options={years}
          placeholder={isArabic ? 'اختر السنة' : 'Select year'}
          title={isArabic ? 'سنة الصنع' : 'Year'}
          testId="car-year-select"
          isArabic={isArabic}
        />
      </Field>

      <Field label={isArabic ? 'المسافة (كم)' : 'Mileage (km)'} isArabic={isArabic}>
        <input
          type="number"
          className="input"
          value={attrs.mileage || ''}
          onChange={e => set('mileage', e.target.value ? Number(e.target.value) : null)}
          placeholder="50000"
          min="0"
          data-testid="car-mileage-input"
          dir={isArabic ? 'rtl' : 'ltr'}
        />
      </Field>

      <Field label={isArabic ? 'نوع الوقود' : 'Fuel Type'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.fuel}
          onChange={v => set('fuel', v)}
          options={FUEL_OPTIONS}
          placeholder={isArabic ? 'اختر نوع الوقود' : 'Select fuel type'}
          title={isArabic ? 'نوع الوقود' : 'Fuel Type'}
          testId="car-fuel-select"
          isArabic={isArabic}
        />
      </Field>

      <Field label={isArabic ? 'ناقل الحركة' : 'Transmission'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.transmission}
          onChange={v => set('transmission', v)}
          options={TRANSMISSION_OPTIONS}
          placeholder={isArabic ? 'اختر ناقل الحركة' : 'Select transmission'}
          title={isArabic ? 'ناقل الحركة' : 'Transmission'}
          testId="car-transmission-select"
          isArabic={isArabic}
        />
      </Field>

      <Field label={isArabic ? 'نوع الهيكل' : 'Body Type'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.body_type}
          onChange={v => set('body_type', v)}
          options={BODY_TYPE_OPTIONS}
          placeholder={isArabic ? 'اختر نوع الهيكل' : 'Select body type'}
          title={isArabic ? 'نوع الهيكل' : 'Body Type'}
          testId="car-body-select"
          isArabic={isArabic}
          showOtherInput={true}
          otherValue={attrs.body_type_other}
          onOtherChange={v => onChange({ ...attrs, body_type_other: v })}
        />
      </Field>

      <Field label={isArabic ? 'الحالة' : 'Condition'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.condition}
          onChange={v => set('condition', v)}
          options={CAR_CONDITION_OPTIONS}
          placeholder={isArabic ? 'اختر الحالة' : 'Select condition'}
          title={isArabic ? 'الحالة' : 'Condition'}
          testId="car-condition-select"
          isArabic={isArabic}
        />
      </Field>
    </div>
  );
}

/* ── Real Estate Form ── */
function PropertyAttrsForm({ attrs, onChange, isArabic }) {
  const set = (key, val) => onChange({ ...attrs, [key]: val });

  return (
    <div className="vertical-attrs-section" data-testid="property-attrs-form">
      <div className="vertical-attrs-header">
        <span className="vertical-attrs-title">
          {isArabic ? 'تفاصيل العقار' : 'Property Details'}
        </span>
      </div>

      <Field label={isArabic ? 'نوع العقار' : 'Property Type'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.property_type}
          onChange={v => set('property_type', v)}
          options={PROPERTY_TYPE_OPTIONS}
          placeholder={isArabic ? 'اختر نوع العقار' : 'Select property type'}
          title={isArabic ? 'نوع العقار' : 'Property Type'}
          testId="property-type-select"
          isArabic={isArabic}
          showOtherInput={true}
          otherValue={attrs.property_type_other}
          onOtherChange={v => onChange({ ...attrs, property_type_other: v })}
        />
      </Field>

      <Field label={isArabic ? 'غرف النوم' : 'Bedrooms'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.bedrooms != null ? String(attrs.bedrooms) : ''}
          onChange={v => set('bedrooms', v ? Number(v) : null)}
          options={['1', '2', '3', '4', '5', '6+']}
          placeholder={isArabic ? 'اختر عدد الغرف' : 'Select bedrooms'}
          title={isArabic ? 'غرف النوم' : 'Bedrooms'}
          testId="property-bedrooms-select"
          isArabic={isArabic}
        />
      </Field>

      <Field label={isArabic ? 'الحمامات' : 'Bathrooms'} isArabic={isArabic}>
        <SheetSelect
          value={attrs.bathrooms != null ? String(attrs.bathrooms) : ''}
          onChange={v => set('bathrooms', v ? Number(v) : null)}
          options={['1', '2', '3', '4', '5+']}
          placeholder={isArabic ? 'اختر عدد الحمامات' : 'Select bathrooms'}
          title={isArabic ? 'الحمامات' : 'Bathrooms'}
          testId="property-bathrooms-select"
          isArabic={isArabic}
        />
      </Field>

      <Field label={isArabic ? 'المساحة (م²)' : 'Area (m²)'} isArabic={isArabic}>
        <input
          type="number"
          className="input"
          value={attrs.area_size || ''}
          onChange={e => set('area_size', e.target.value ? Number(e.target.value) : null)}
          placeholder="120"
          min="0"
          data-testid="property-area-input"
          dir={isArabic ? 'rtl' : 'ltr'}
        />
      </Field>
    </div>
  );
}

/* ── Main Exported Component ── */
export default function VerticalAttrsForm({ category, subcategory, attrs, onChange, isArabic }) {
  // iter268: pass BOTH parent and sub to detectVertical so the gating
  // is strict — phone form only when parent is mobile_accessories or
  // sub is smartphones/tablets/phone_accessories; never on plain
  // 'electronics'.
  const vertical = detectVertical(category, subcategory);
  if (!vertical) return null;

  if (vertical === 'phone') {
    return <PhoneAttrsForm attrs={attrs.phone_attrs || {}} onChange={v => onChange({ ...attrs, phone_attrs: v })} isArabic={isArabic} />;
  }
  if (vertical === 'car') {
    return <CarAttrsForm attrs={attrs.car_attrs || {}} onChange={v => onChange({ ...attrs, car_attrs: v })} isArabic={isArabic} subcategory={subcategory} />;
  }
  if (vertical === 'real_estate') {
    return <PropertyAttrsForm attrs={attrs.property_attrs || {}} onChange={v => onChange({ ...attrs, property_attrs: v })} isArabic={isArabic} />;
  }
  return null;
}
